import axios from "axios";
import {
  ASSIGN_ADDENDUM,
  LIST_ALL_CONTRACTS_FOR_ADDENDUM,
  UNASSIGN_ADDENDUM,
  LIST_ALL_TEMPLATES_FOR_ENTITY_FORM,
} from "@/utils/constants/constants.js";

export default {
  data() {
    return {
      select_template_id: null,
      selected_template: null,
      modal_template: null,
      table_safe_contracts: [],
      contracts_by_template: {},
      templates_by_form: {},
      entity_type_id: null,
      headers: {},
    };
  },
  methods: {
    // Fetch all contracts for an addendum
    async fetch_contracts(addendum_id) {
      this.select_template_id = addendum_id;
      const matched_template = this.manage_template?.find(t => t.id === addendum_id);
      const addendum_entity_type_id = matched_template?.entity_type_id ?? this.entity_type_id;
      if (matched_template) this.selected_template = { ...matched_template };

      // restrict eligible contracts to those already assigned to the same entity type
      const allowed_contract_ids = new Set(
        (this.manage_template || [])
          .filter(t =>
            t.template_type === "contract" &&
            (t.entity_type_id === undefined || t.entity_type_id === null || Number(t.entity_type_id) === Number(addendum_entity_type_id))
          )
          .map(t => t.id)
      );

      this.table_safe_contracts = [];

      const toBoolean = val => val === true || val === "true" || val === 1 || val === "1";

      try {
        // Fetch all candidate contracts for this addendum and entity type
        const { data: resp } = await axios.get(LIST_ALL_CONTRACTS_FOR_ADDENDUM, {
          params: {
            addendum_entity_type_id: addendum_id,
            entity_type_id: this.entity_type_id
          },
          headers: this.headers,
        });

        if (resp?.status !== "success") {
          this.show_error(resp?.message || "Failed to fetch contracts");
          return;
        }

        const contracts = (resp.data || [])
          .map(contract => {
            const id = contract.contract_entity_type_id;
            const entity_id = contract.entity_type_id ?? this.entity_type_id;
            const contract_template = this.manage_template?.find(t => t.id === id);
            const contract_entity_type_id = contract_template?.entity_type_id ?? entity_id;
            const addendum_candidates = [
              contract.addendum_entity_type_id,
              contract.addendum_id,
              contract.addendum_template_entity_type_id,
              contract.parent_template_entity_type_id,
            ].filter(v => v !== undefined && v !== null);

            const belongs_to_addendum =
              addendum_candidates.length === 0
                ? true // keep if API omits link to avoid hiding valid rows
                : addendum_candidates.some(val => Number(val) === Number(addendum_id));

            const belongs_to_entity =
              contract_entity_type_id === undefined ||
              contract_entity_type_id === null ||
              Number(contract_entity_type_id) === Number(addendum_entity_type_id);

            return {
              id,
              template_name: contract.contract_name,
              addendum_entity_type_id: addendum_id,
              entity_type_id: entity_id,
              is_assigned: toBoolean(contract.is_assigned),
              isLoading: false,
              belongs_to_addendum,
              belongs_to_entity,
            };
          })
          // keep only contracts for this entity/addendum AND present in allowed set, but always retain ones already assigned
          .filter(c =>
            c.is_assigned ||
            (c.belongs_to_addendum && c.belongs_to_entity && (allowed_contract_ids.size === 0 || allowed_contract_ids.has(c.id)))
          );

        this.contracts_by_template[addendum_id] = contracts;
        this.table_safe_contracts = contracts;
      } catch (err) {
        this.show_error("Error fetching contracts: " + (err.response?.data?.message || err.message));
      }
    },

    // Fetch templates for a given form and entity_type
    async fetch_templates_for_form(form_id) {
      if (!form_id || !this.entity_type_id) {
        this.show_error("form_id and entity_type_id are required to fetch templates.");
        return;
      }

      try {
        const { data: resp } = await axios.get(LIST_ALL_TEMPLATES_FOR_ENTITY_FORM, {
          params: { form_id, entity_type_id: this.entity_type_id },
          headers: this.headers,
        });

        if (resp?.status !== "success") {
          this.show_error(resp?.message || "Failed to fetch templates.");
          return;
        }

        this.templates_by_form[form_id] = resp.data.map(t => ({
          template_entity_type_id: t.template_entity_type_id,
          template_name: t.template_name,
          is_assigned: t.is_assigned,
        }));
      } catch (err) {
        this.show_error("Error fetching templates: " + (err.response?.data?.message || err.message));
      }
    },

    async assign_contract(row) {
      row.is_loading = true;
      try {
        await axios.post(
          ASSIGN_ADDENDUM,
          {
            contract_entity_type_id: row.id,
            addendum_entity_type_id: this.select_template_id,
            entity_type_id: this.entity_type_id
          },
          { headers: this.headers }
        );
        row.is_assigned = true;
        this.show_success(`Assigned ${row.template_name} successfully.`);
        await this.refresh_contracts();
      } catch (err) {
        row.is_assigned = false;
        this.show_error("Assignment failed: " + (err.response?.data?.message || err.message));
      } finally {
        row.is_loading = false;
      }
    },

    async unassign_contract(row) {
      row.is_loading = true;
      try {
        await axios.post(
          UNASSIGN_ADDENDUM,
          {
            contract_entity_type_id: row.id,
            addendum_entity_type_id: this.select_template_id,
            entity_type_id: this.entity_type_id
          },
          { headers: this.headers }
        );
        row.is_assigned = false;
        this.show_success(`Unassigned ${row.template_name} successfully.`);
        await this.refresh_contracts();
      } catch (err) {
        row.is_assigned = true;
        this.show_error("Unassignment failed: " + (err.response?.data?.message || err.message));
      } finally {
        row.is_loading = false;
      }
    },

    open_assign_contracts_modal(template) {
      this.modal_template = { ...template };
      this.table_safe_contracts = this.contracts_by_template[template.id] || [];
      this.fetch_contracts(template.id);
    },

    async refresh_contracts() {
      if (this.select_template_id) {
        await this.fetch_contracts(this.select_template_id);
        if (typeof this.fetch_linked_contracts === "function") {
          await this.fetch_linked_contracts(this.select_template_id);
        }
      }
    },
  },
};
